/**
 * ChatService: select provider cards, add/edit modal, and list of saved tokens.
 * Reusable in Onboarding (step 2) and Admin Hub (Manage Chat Service tab).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import {
  fetchTokenDetails,
  deleteTokenDetails,
} from '../../services/chatServiceTokenService';
import type { TokenDetailItem } from '../../services/chatServiceTokenService';
import { CHAT_SERVICE_PROVIDERS } from './constants';
import type { ChatServiceProviderId } from './types';
import type { SavedTokenDisplay } from './types';
import ChatServiceCard from './ChatServiceCard';
import ChatServiceModal from './ChatServiceModal';
import SavedTokenList from './SavedTokenList';
import { useToast } from '../../contexts/ToastContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';

export interface ChatServiceProps {
  companyId: string;
  onComplete?: () => void;
  /** When true, parent owns bottom actions (e.g. single Continue in onboarding bar); no Skip/Continue inside card */
  hideBottomActions?: boolean;
  /** @deprecated Use hideBottomActions instead. Kept so callers passing showSkip do not cause ReferenceError. */
  showSkip?: boolean;
  /** Called when saved tokens list changes so parent can enable/disable Continue (mandatory step) */
  onTokensChange?: (hasToken: boolean) => void;
  mode?: 'onboarding' | 'admin';
}

/** Normalize API token_details to SavedTokenDisplay[]; merges in data-level vault IDs when missing on item */
function normalizeTokenDetails(
  tokenDetails: TokenDetailItem | TokenDetailItem[] | Record<string, unknown>,
  dataIds?: { vault_unique_id?: string; giggso_vault_id?: string }
): SavedTokenDisplay[] {
  const list: SavedTokenDisplay[] = [];
  let arr: TokenDetailItem[] = [];
  if (Array.isArray(tokenDetails)) {
    arr = tokenDetails;
  } else if (tokenDetails && typeof tokenDetails === 'object' && !Array.isArray(tokenDetails)) {
    const obj = tokenDetails as Record<string, unknown>;
    if (obj.vault_unique_id != null || obj.token_type != null) {
      arr = [tokenDetails as TokenDetailItem];
    } else {
      arr = Object.values(obj).filter((v) => v && typeof v === 'object') as TokenDetailItem[];
    }
  }
  for (const item of arr) {
    const t = item as TokenDetailItem & { name?: string; tokenName?: string };
    // Prefer token name (name or tokenName from API) for accordion header; fallback to provider type
    const tokenNameForHeader = t.name ?? t.tokenName ?? t.token_type ?? 'Unnamed';
    list.push({
      vault_unique_id: t.vault_unique_id ?? dataIds?.vault_unique_id,
      giggso_vault_id: t.giggso_vault_id ?? dataIds?.giggso_vault_id,
      token_type: t.token_type ?? '—',
      name: t.name ?? t.tokenName,
      displayLabel: tokenNameForHeader,
      raw: t,
    });
  }
  return list;
}

const ChatService: React.FC<ChatServiceProps> = ({
  companyId,
  onComplete,
  hideBottomActions = false,
  showSkip: _showSkip = false,
  onTokensChange,
  mode = 'onboarding',
}) => {
  const { showSuccess, showError } = useToast();
  const [savedTokens, setSavedTokens] = useState<SavedTokenDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<ChatServiceProviderId | null>(null);
  const [editToken, setEditToken] = useState<SavedTokenDisplay | null>(null);
  const [deleteConfirmToken, setDeleteConfirmToken] = useState<SavedTokenDisplay | null>(null);

  const loadTokens = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await fetchTokenDetails(companyId);
      if (res.success && res.data?.token_details) {
        setSavedTokens(
          normalizeTokenDetails(res.data.token_details, {
            vault_unique_id: res.data.vault_unique_id,
            giggso_vault_id: res.data.giggso_vault_id,
          })
        );
      } else {
        setSavedTokens([]);
      }
    } catch {
      setSavedTokens([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  // Notify parent when token list changes (for mandatory step: disable Continue until added)
  useEffect(() => {
    onTokensChange?.(savedTokens.length > 0);
  }, [savedTokens.length, onTokensChange]);

  const handleCardClick = (provider: ChatServiceProviderId) => {
    setEditToken(null);
    setSelectedProvider(provider);
    setModalOpen(true);
  };

  const handleEdit = (token: SavedTokenDisplay) => {
    const normalized = (token.token_type ?? 'chatgpt').toLowerCase().replace(/\s/g, '');
    if (normalized === 'azureopenai') {
      setSelectedProvider('AzureOpenAI');
    } else if (normalized === 'gemini') {
      setSelectedProvider('Gemini');
    } else {
      setSelectedProvider('ChatGPT');
    }
    setEditToken(token);
    setModalOpen(true);
  };

  const handleDeleteClick = (token: SavedTokenDisplay) => {
    setDeleteConfirmToken(token);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmToken || !companyId) return;
    try {
      await deleteTokenDetails({
        companyId,
        vault_unique_id: deleteConfirmToken.vault_unique_id,
      });
      showSuccess('Chat service connection removed');
      setDeleteConfirmToken(null);
      loadTokens();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleModalSaved = () => {
    loadTokens();
  };

  // One GPT per company: show selection cards only when no saved token; after save show list only; after delete show cards again
  const hasSavedToken = savedTokens.length > 0;

  return (
    <Box>
      {!hasSavedToken && (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Select the chat service you'd like to connect to
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 2,
              mb: 3,
            }}
          >
            {CHAT_SERVICE_PROVIDERS.map((option) => (
              <ChatServiceCard
                key={option.id}
                option={option}
                onClick={() => handleCardClick(option.id)}
              />
            ))}
          </Box>
        </>
      )}

      <SavedTokenList
        tokens={savedTokens}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
      />

      {/* Skip/Continue only when parent does not own bottom bar (e.g. Admin Hub) */}
      {!hideBottomActions && onComplete && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={onComplete}>
            Continue
          </Button>
        </Box>
      )}

      {selectedProvider && (
        <ChatServiceModal
          open={modalOpen}
          onClose={() => { setModalOpen(false); setSelectedProvider(null); setEditToken(null); }}
          provider={selectedProvider}
          companyId={companyId}
          editToken={editToken}
          onSaved={handleModalSaved}
        />
      )}

      <Dialog open={Boolean(deleteConfirmToken)} onClose={() => setDeleteConfirmToken(null)}>
        <DialogTitle>Delete Chat Service?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove &quot;{deleteConfirmToken?.displayLabel}&quot;? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmToken(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatService;
